import React, { FC, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import CircularProgress from '@mui/material/CircularProgress'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import Alert from '@mui/material/Alert'
import { Video } from '../../types'
import { Language } from '../../system/apitools'
import SectionWrapper, { SectionStatus } from './LockedSectionWrapper'

interface TranslationsSectionProps {
  video: Video
  languages: Language[]
  translationProgress: { percent?: number; stage?: string; logs?: string[] } | null
  onTranslate: (langs: string[]) => void
  isStarting: boolean
  // True once this video is sealed — explicitly, or because the correction
  // window has expired. StartTranslation is refused server-side once sealed
  // (guardSealed(v, now, "subtitles")), so the control freezes here rather
  // than letting an admin pick languages that only fail to start on submit.
  // Translations already produced stay visible — only starting a new one
  // is a write.
  sealed: boolean
}

const TranslationsSection: FC<TranslationsSectionProps> = ({
  video,
  languages,
  translationProgress,
  onTranslate,
  isStarting,
  sealed,
}) => {
  const logEndRef = useRef<HTMLDivElement>(null)
  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [translationProgress?.logs?.length])

  const masterLang = video.subtitles?.master_language
  const isTranslating = video.translation_state === 'translating'
  const translatedAt = video.translated_at || {}

  // Only offer languages other than the master.
  const options = languages.filter(l => l.code !== masterLang)

  let status: SectionStatus = 'pending'
  if (isTranslating) status = 'in_progress'
  else if (Object.keys(translatedAt).length > 0) status = 'complete'

  const toggle = (code: string) => {
    setSelected(prev => (prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]))
  }

  if (!video.subtitles) {
    return (
      <SectionWrapper title="Translations" status="pending">
        <Alert severity="info">
          Generate or upload subtitles first — translations are derived from the master subtitle track.
        </Alert>
      </SectionWrapper>
    )
  }

  return (
    <SectionWrapper title="Translations" status={status}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Master language: <strong>{languages.find(l => l.code === masterLang)?.name || masterLang}</strong>
        {' '}(set in the Language dropdown above). Select languages to translate into and click Translate.
      </Typography>

      {sealed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This video is sealed: the manifest attests to the subtitle text in every published
          language, so no new translation can be started. Translations already produced stay
          here to view and download.
        </Alert>
      )}

      {!isTranslating && !sealed && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {options.map(l => {
            const done = translatedAt[l.code]
            return (
              <Chip
                key={l.code}
                label={done ? `${l.name} ✓` : l.name}
                color={selected.includes(l.code) ? 'primary' : 'default'}
                variant={selected.includes(l.code) ? 'filled' : 'outlined'}
                onClick={() => toggle(l.code)}
                title={done ? `Last translated: ${new Date(done).toLocaleString()}` : undefined}
              />
            )
          })}
        </Box>
      )}

      {!isTranslating && sealed && Object.keys(translatedAt).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {options.filter(l => translatedAt[l.code]).map(l => (
            <Chip
              key={l.code}
              label={`${l.name} ✓`}
              variant="outlined"
              title={`Last translated: ${new Date(translatedAt[l.code]).toLocaleString()}`}
            />
          ))}
        </Box>
      )}

      {!isTranslating && !sealed && (
        <Button
          variant="contained"
          color="secondary"
          disabled={isStarting || selected.length === 0}
          onClick={() => onTranslate(selected)}
        >
          {isStarting ? 'Starting...' : `Translate (${selected.length})`}
        </Button>
      )}

      {isTranslating && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">{translationProgress?.stage || 'Processing...'}</Typography>
          </Box>
          {translationProgress?.percent !== undefined && translationProgress.percent > 0 && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress variant="determinate" value={translationProgress.percent} />
              <Typography variant="caption" color="text.secondary">{translationProgress.percent}%</Typography>
            </Box>
          )}
          {translationProgress?.logs && translationProgress.logs.length > 0 && (
            <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: '#1a1a1a', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {translationProgress.logs.map((line, i) => (
                <div key={i} style={{ color: '#aaa', whiteSpace: 'pre' }}>{line}</div>
              ))}
              <div ref={logEndRef} />
            </Box>
          )}
        </Box>
      )}

      {video.translation_state === 'translate_failed' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <ErrorOutlineIcon color="error" fontSize="small" />
          <Typography variant="body2" color="error">
            Translation failed: {video.translation_error || 'Unknown error'}
          </Typography>
        </Box>
      )}
    </SectionWrapper>
  )
}

export default TranslationsSection
